import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPostNoContent } from '@/services/apiClient';
import type {
  AccountOption,
  Attendee,
  AttendeesRequest,
  DashboardEvent,
  DashboardEventsRequest,
  EventDetails,
  EventAttendeesListApiRequest,
  EventAttendeesListRequestOptions,
  EventSyncRequest,
  EventSyncResponse,
  GraphUpdates,
  ListResponse,
  PendingJobToUnlock,
  PendingJobsSummary,
  ProfileSyncRequest,
  UnlockSyncRequest,
} from '@/types/api.types';

const ATTENDEES_EXPORT_PAGE_SIZE = 1000;
const ACTIVE_SYNC_POLL_INTERVAL_MS = 1_000;
const IDLE_EVENT_DETAILS_POLL_INTERVAL_MS = 30_000;

export function isActiveSyncStatus(status: string | undefined): boolean {
  const normalizedStatus = status?.trim().toLowerCase();
  return normalizedStatus === 'queued'
    || normalizedStatus === 'retrieving data'
    || normalizedStatus === 'in progress'
    || normalizedStatus === 'inprogress';
}

function buildEventAttendeesListRequest(
  options: EventAttendeesListRequestOptions,
): EventAttendeesListApiRequest {
  return {
    eventCode: options.eventCode,
    pageNumber: options.pageNumber,
    pageSize: options.pageSize,
    totalRecords: options.totalRecords ?? 0,
    sortColumn: options.sortColumn ?? '',
    sortDirection: options.sortDirection ?? '',
    searchParam: options.searchParam ?? '',
    attendeesGridPostFilters: [],
  };
}

function normalizeAttendeesResponse(payload: unknown): ListResponse<Attendee> {
  if (!payload || typeof payload !== 'object') {
    return {
      pageNumber: 1,
      pageSize: 10,
      totalRecords: 0,
      data: [],
    };
  }

  const response = payload as Record<string, unknown>;
  const candidates = [
    response.data,
    response.lstEventAttendees,
    response.lstAttendees,
    response.items,
  ];

  const rows = candidates.find((value) => Array.isArray(value));
  const attendeeRows = (Array.isArray(rows) ? rows : []) as Attendee[];

  let totalRecords = attendeeRows.length;
  if (typeof response.totalRecords === 'number') {
    totalRecords = response.totalRecords;
  } else if (typeof response.totalCount === 'number') {
    totalRecords = response.totalCount;
  } else if (typeof response.count === 'number') {
    totalRecords = response.count;
  }

  const pageNumber = typeof response.pageNumber === 'number' ? response.pageNumber : 1;
  const pageSize = typeof response.pageSize === 'number' ? response.pageSize : Math.max(attendeeRows.length, 10);

  return {
    pageNumber,
    pageSize,
    totalRecords,
    message: typeof response.message === 'string' ? response.message : undefined,
    data: attendeeRows,
  };
}

function getEventSyncPath(request: EventSyncRequest): string {
  const params = new URLSearchParams({
    eventCode: request.eventCode,
    accountName: request.accountName,
  });
  return `Events/EventSync?${params.toString()}`;
}

export function useDashboardEventsQuery(request: DashboardEventsRequest, isReady = true) {
  const canSearch = !request.searchEventText || request.searchEventText.length >= 3;
  return useQuery({
    queryKey: [
      'registrations', 'dashboard', 'events',
      request.startDate, request.endDate, request.searchEventText,
      request.accountCode, request.pageNumber, request.pageSize,
      request.sortColumn, request.sortDirection, request.filters,
    ],
    queryFn: () =>
      apiPost<DashboardEventsRequest, ListResponse<DashboardEvent>>('Events/GetEventsList', request),
    enabled: canSearch && isReady,
    placeholderData: (previousData) => previousData,
  });
}

export function useAccountsQuery() {
  return useQuery({
    queryKey: ['registrations', 'dashboard', 'accounts'],
    queryFn: () => apiGet<AccountOption[]>('Events/GetAccounts'),
  });
}

export function usePendingJobsSummaryQuery() {
  return useQuery({
    queryKey: ['registrations', 'dashboard', 'pending-summary'],
    queryFn: () => apiGet<PendingJobsSummary>('Events/GetAllPendingJobsAsync'),
    refetchInterval: ACTIVE_SYNC_POLL_INTERVAL_MS,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function usePendingJobsToUnlockQuery(isEnabled: boolean) {
  return useQuery({
    queryKey: ['registrations', 'dashboard', 'pending-unlock'],
    queryFn: () => apiGet<PendingJobToUnlock[]>('Events/GetPendingJobsToUnlockSync'),
    enabled: isEnabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useEventSyncMutation() {
  return useMutation({
    mutationKey: ['registrations', 'dashboard', 'event-sync'],
    mutationFn: (request: EventSyncRequest) =>
      apiGet<EventSyncResponse>(getEventSyncPath(request)),
  });
}

export function useEventDetailsQuery(eventCode: string) {
  return useQuery({
    queryKey: ['registrations', 'event', 'details', eventCode],
    queryFn: () => apiGet<EventDetails>(`Events/GetEventDetailsByCode?eventCode=${encodeURIComponent(eventCode)}`),
    enabled: !!eventCode,
    refetchInterval: (query) => isActiveSyncStatus(query.state.data?.syncStatus)
      ? ACTIVE_SYNC_POLL_INTERVAL_MS
      : IDLE_EVENT_DETAILS_POLL_INTERVAL_MS,
  });
}

export function useEventAttendeesQuery(request: AttendeesRequest, isSyncActive = false) {
  const { eventCode, pageNumber, pageSize, searchParam, sortColumn, sortDirection } = request;
  return useQuery({
    queryKey: [
      'registrations', 'event', 'attendees', eventCode, pageNumber, pageSize,
      searchParam ?? '', sortColumn ?? '', sortDirection ?? '',
    ],
    queryFn: async () => {
      const requestPayload = buildEventAttendeesListRequest({
        eventCode,
        pageNumber,
        pageSize,
        searchParam,
        sortColumn,
        sortDirection,
      });

      const response = await apiPost<EventAttendeesListApiRequest, unknown>(
        'Events/GetEventAttendeesList',
        requestPayload,
      );

      return normalizeAttendeesResponse(response);
    },
    enabled: !!eventCode,
    refetchInterval: isSyncActive ? ACTIVE_SYNC_POLL_INTERVAL_MS : false,
  });
}

async function fetchEventAttendeesList(request: EventAttendeesListApiRequest): Promise<ListResponse<Attendee>> {
  const response = await apiPost<EventAttendeesListApiRequest, unknown>(
    'Events/GetEventAttendeesList',
    request,
  );
  return normalizeAttendeesResponse(response);
}

export function useEventAttendeesExportMutation() {
  return useMutation({
    mutationKey: ['registrations', 'event', 'attendees-export'],
    mutationFn: async (request: Pick<AttendeesRequest, 'eventCode' | 'searchParam'>) => {
      const attendees: Attendee[] = [];
      let pageNumber = 1;
      let totalRecords = 0;

      for (;;) {
        const payload = buildEventAttendeesListRequest({
          eventCode: request.eventCode,
          pageNumber,
          pageSize: ATTENDEES_EXPORT_PAGE_SIZE,
          totalRecords,
          searchParam: request.searchParam,
        });

        const page = await fetchEventAttendeesList(payload);
        if (pageNumber === 1) {
          totalRecords = page.totalRecords;
        }

        if (page.data.length === 0) {
          break;
        }

        attendees.push(...page.data);

        if (attendees.length >= totalRecords || page.data.length < ATTENDEES_EXPORT_PAGE_SIZE) {
          break;
        }

        pageNumber += 1;
        if (pageNumber > 1000) {
          break;
        }
      }

      return totalRecords > 0 ? attendees.slice(0, totalRecords) : attendees;
    },
  });
}

export function useEventGraphUpdatesQuery(
  accountCode: string | undefined,
  eventCode: string,
  isSyncActive = false,
) {
  return useQuery({
    queryKey: ['registrations', 'event', 'graph', accountCode, eventCode],
    queryFn: () => apiGet<GraphUpdates>(`Events/GetGraphUpdates?accountCode=${encodeURIComponent(accountCode!)}&eventCode=${encodeURIComponent(eventCode)}`),
    enabled: !!accountCode && !!eventCode,
    refetchInterval: isSyncActive ? ACTIVE_SYNC_POLL_INTERVAL_MS : false,
  });
}

export function useProfileSyncMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['registrations', 'event', 'profile-sync'],
    mutationFn: (request: ProfileSyncRequest) => apiPost<ProfileSyncRequest, string>('Events/SyncAttendees', request),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'event', 'details', variables.eventCode] });
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'event', 'graph', variables.accountCode, variables.eventCode] });
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'event', 'attendees', variables.eventCode] });
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'dashboard', 'pending-summary'] });
    },
  });
}

export function useUnlockSyncMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['registrations', 'event', 'unlock-sync'],
    mutationFn: async (request: UnlockSyncRequest) => {
      await apiPostNoContent('Events/UnlockSyncJob', request);
      return true;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'event', 'details', variables.eventCode] });
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'dashboard', 'pending-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'dashboard', 'pending-unlock'] });
    },
  });
}
